import { formatKpi, Kpi, ValueType } from '../../Formatters';
import {
  ColumnHeaderForPA,
  ProductGroupKpi,
  ProductGroupKpiPercentage,
  RevenuePriceType,
  RevenueShareType,
} from '@models/PerformanceAnalysisboard';
import { ColumnHeaderForOR as OppColumnHeader, ProductGroupKpi as OppKpi } from '@models/OpportunityReview';
import { ColumnHeader as ProductColumnHeader } from '@models/Products';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import OverIcon from '@assets/over.svg';
import UnderIcon from '@assets/under.svg';
import NeutralIcon from '@assets/neutral.svg';
import ClearNegativeIcon from '@assets/clear-negative.svg';
import ClearPositiveIcon from '@assets/clear-positive.svg';
import SlightNegativePositiveIcon from '@assets/slight-negative-positive.svg';
import * as oppStyles from '@components/opportunity-review-board/OpportunityReviewBoard.module.scss';
import { isKpiShare } from '../../TableUtils';
import { DisplayOptions } from '@components/top-selling-products/products-table-filter/Types';
import { ShareType } from '@utils/pageConfig/Types';

export const volumeType = Kpi.salesVolume;
export const revenueType = Kpi.revenue;
export const revenueShareType = Kpi.revenueShare;
export const isPositive = (value: number) => value > 0;
export const isZeroGrowth = (value: number) => value === 0;

const isRevenueShareMixColumns = (headerKey: string) => {
  return headerKey === ColumnHeaderForPA.yourRevenueShare || headerKey === ColumnHeaderForPA.yourRevenueMix;
};

const isOppShare = (headerKey: string) => {
  return headerKey === OppColumnHeader.myRevenueShare || headerKey === OppColumnHeader.myVolumeShare;
};

const isProductsShare = (headerKey: string) => {
  return (
    headerKey === ProductColumnHeader.marketShareRevenue ||
    headerKey === ProductColumnHeader.marketShareVolume ||
    headerKey === ProductColumnHeader.retailerShareRevenue ||
    headerKey === ProductColumnHeader.retailerShareVolume ||
    headerKey === ProductColumnHeader.retailerMixRevenue ||
    headerKey === ProductColumnHeader.retailerMixVolume
  );
};

export const isGrowthFairShareString = (headerKey: string) => {
  return (
    headerKey === OppColumnHeader.overUnderFairShareRevenue ||
    headerKey === OppColumnHeader.overUnderFairShareVolume ||
    headerKey === OppColumnHeader.overUnderGrowthRevenue ||
    headerKey === OppColumnHeader.overUnderGrowthVolume
  );
};

export const isGrowthShareOpp = (headerKey: string) => {
  return (
    headerKey === OppColumnHeader.growthOpportunityRevenue ||
    headerKey === OppColumnHeader.growthOpportunityVolume ||
    headerKey === OppColumnHeader.fairShareOpportunityRevenue ||
    headerKey === OppColumnHeader.fairShareOpportunityVolume
  );
};

export const isRetailerRank = (headerKey: string) => {
  return headerKey === ProductColumnHeader.retailerRank;
};

export const isMarketRank = (headerKey: string) => {
  return headerKey === ProductColumnHeader.marketRank;
};

export const isProductFeatureViewColumn = (headerKey: string) => {
  return headerKey === ProductColumnHeader.firstActivity || headerKey.includes(ProductColumnHeader.feature);
};

export const getDisplayValueForRevenueVolume = (
  headerKey: string,
  performanceAnalysisboardPg: any,
  isPAOrCL: boolean,
  selectedShareType?: ShareType,
) => {
  if (isProductsShare(headerKey)) {
    const growthPercentage = performanceAnalysisboardPg[headerKey as ProductColumnHeader]?.growthPercentage;

    return !growthPercentage ? 0 : +growthPercentage.toFixed(1);
  }

  if (isOppShare(headerKey)) {
    const growthPercentage = performanceAnalysisboardPg[headerKey as OppColumnHeader]?.growthPercentage;
    return !growthPercentage ? 0 : +growthPercentage.toFixed(1);
  }

  if (isRevenueShareMixColumns(headerKey)) {
    // Under certain circumstances, the display value might be 0.001 and we still treat it as zero after round up to 1 decimal place
    return getRevenueVolumeGrowthPercentage(performanceAnalysisboardPg, isPAOrCL, true, selectedShareType);
  }
  return getRevenueVolumeGrowthPercentage(performanceAnalysisboardPg, isPAOrCL, false, selectedShareType);
};

export const getRevenueVolumeGrowthPercentage = (
  performanceAnalysisboardPg: any,
  isForPA: boolean,
  isRevenue: boolean,
  selectedShareType?: ShareType,
) => {
  let finalList: string[];

  if (isRevenue) {
    finalList = revenueKpiList;
  } else {
    finalList = volumeKpiList;
  }
  let revenueVolumeShareMixGrowthPercentage;
  if (isForPA) {
    if (selectedShareType === ShareType.MyMix) {
      revenueVolumeShareMixGrowthPercentage = performanceAnalysisboardPg[finalList[0]]?.growthPercentage;
      return revenueVolumeShareMixGrowthPercentage;
    }
    revenueVolumeShareMixGrowthPercentage = performanceAnalysisboardPg[finalList[1]]?.growthPercentage;
    return revenueVolumeShareMixGrowthPercentage;
  } else {
    revenueVolumeShareMixGrowthPercentage = performanceAnalysisboardPg[finalList[2]]?.growthPercentage;
  }
  return !revenueVolumeShareMixGrowthPercentage ? 0 : +revenueVolumeShareMixGrowthPercentage.toFixed(1);
};

export const displaySecColorForZeroGrowth = (displayValue: number) => {
  return isZeroGrowth(displayValue) ? 'grey' : 'red';
};

export const displaySecColorForPositiveGrowth = (
  headerKey: string,
  performanceAnalysisboardPg: any,
  selectedGrowthType: GrowthTypes,
  isPAOrCL: boolean, //PA & CL page needs special handling for Share & Mix KPI data. This boolean is to check whether we're consuming these function from PA/CL page
  selectedShareType?: ShareType,
) => {
  let displayValue;
  if (RevenueShareType.includes(headerKey) || isProductsShare(headerKey) || isOppShare(headerKey)) {
    displayValue = getDisplayValueForRevenueVolume(headerKey, performanceAnalysisboardPg, isPAOrCL, selectedShareType);
  } else {
    displayValue =
      selectedGrowthType === GrowthTypes.actual
        ? performanceAnalysisboardPg[headerKey as ProductGroupKpi]?.actual
        : performanceAnalysisboardPg[headerKey as ProductGroupKpi]?.percentage;
  }
  return isPositive(displayValue) ? 'green' : displaySecColorForZeroGrowth(displayValue);
};

export const isVolumeColumn = (headerKey: string) => {
  return (
    headerKey === ColumnHeaderForPA.marketVolume ||
    headerKey === ColumnHeaderForPA.yourVolume ||
    headerKey === ColumnHeaderForPA.marketExcludeRetailVolume ||
    headerKey === OppColumnHeader.myVolume ||
    headerKey === OppColumnHeader.growthOpportunityVolume ||
    headerKey === OppColumnHeader.fairShareOpportunityVolume ||
    headerKey === ProductColumnHeader.retailerVolume
  );
};

const getSwotWithIcon: any = (headerKey: string, content: any) => {
  if (content[headerKey as OppKpi] === null) {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div>{'--'}</div>
      </div>
    );
  } else if (content[headerKey as OppKpi]?.swotStrength === 'Slight') {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div>{content[headerKey as OppKpi]?.value}</div>
        <div>
          <img
            src={SlightNegativePositiveIcon}
            alt={content[headerKey as OppKpi]?.value}
            className={oppStyles.OpportunityReviewBoard__TableCell_WithIconRight}
          />
        </div>
      </div>
    );
  } else if (content[headerKey as OppKpi]?.swotStrength === 'Strong') {
    if (content[headerKey as OppKpi]?.value === 'Threat' || content[headerKey as OppKpi]?.value === 'Weakness') {
      return (
        <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
          <div>{content[headerKey as OppKpi]?.value}</div>
          <div>
            <img
              src={ClearNegativeIcon}
              alt={content[headerKey as OppKpi]?.value}
              className={oppStyles.OpportunityReviewBoard__TableCell_WithIconRight}
            />
          </div>
        </div>
      );
    } else {
      return (
        <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
          <div>{content[headerKey as OppKpi]?.value}</div>
          <div>
            <img
              src={ClearPositiveIcon}
              alt={content[headerKey as OppKpi]?.value}
              className={oppStyles.OpportunityReviewBoard__TableCell_WithIconRight}
            />
          </div>
        </div>
      );
    }
  } else {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div>{content[headerKey as OppKpi]?.value}</div>
        <div>
          <img
            src={NeutralIcon}
            alt={content[headerKey as OppKpi]?.value}
            className={oppStyles.OpportunityReviewBoard__TableCell_WithIconRight}
          />
        </div>
      </div>
    );
  }
};

const getGrowthShareOppWithIcon: any = (headerKey: string, content: any) => {
  if (content[headerKey as OppKpi] === null) {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div className={oppStyles.OpportunityReviewBoard__TableCell_GrowthShareText}>{'--'}</div>
      </div>
    );
  } else if (content[headerKey as OppKpi] === 'Over') {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div>
          <img
            src={OverIcon}
            alt={content[headerKey as OppKpi]}
            className={oppStyles.OpportunityReviewBoard__TableCell_WithIconLeft}
          />
        </div>
        <div className={oppStyles.OpportunityReviewBoard__TableCell_GrowthShareText}>
          {content[headerKey as OppKpi]}
        </div>
      </div>
    );
  } else if (content[headerKey as OppKpi] === 'Under') {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div>
          <img
            src={UnderIcon}
            alt={content[headerKey as OppKpi]}
            className={oppStyles.OpportunityReviewBoard__TableCell_WithIconLeft}
          />
        </div>
        <div className={oppStyles.OpportunityReviewBoard__TableCell_GrowthShareText}>
          {content[headerKey as OppKpi]}
        </div>
      </div>
    );
  } else {
    return (
      <div className={oppStyles.OpportunityReviewBoard__TableCell_IconAndText}>
        <div>
          <img
            src={NeutralIcon}
            alt={content[headerKey as OppKpi]}
            className={oppStyles.OpportunityReviewBoard__TableCell_WithIconLeft}
          />
        </div>
        <div className={oppStyles.OpportunityReviewBoard__TableCell_GrowthShareText}>
          {content[headerKey as OppKpi]}
        </div>
      </div>
    );
  }
};

export const displayPrimaryCellSingleValue = (headerKey: string, content: any, currency: string) => {
  if (headerKey === OppColumnHeader.swotCategoryRevenue || headerKey === OppColumnHeader.swotCategoryVolume) {
    return getSwotWithIcon(headerKey, content);
  }

  if (isGrowthFairShareString(headerKey)) {
    return getGrowthShareOppWithIcon(headerKey, content);
  }

  if (isGrowthShareOpp(headerKey)) {
    const kpiValue = content[headerKey as ProductGroupKpi]?.value ?? '--';

    if (kpiValue === '--') {
      return kpiValue;
    }
    if (isVolumeColumn(headerKey)) {
      return formatKpi(volumeType, kpiValue);
    }
    return formatKpi(revenueType, kpiValue, {
      currencySymbol: currency,
    });
  }
  return '';
};

const formatProductsKpiForSharePercentage = (columnHeader: ProductColumnHeader, productsPg: any): string => {
  return formatKpi(revenueShareType, productsPg[columnHeader]?.sharePercentage ?? 0);
};

const displayProductsCell = (headerKey: string, productsPg: any) => {
  switch (headerKey) {
    case ProductColumnHeader.retailerMixRevenue:
      return formatProductsKpiForSharePercentage(ProductColumnHeader.retailerMixRevenue, productsPg);
    case ProductColumnHeader.marketShareRevenue:
      return formatProductsKpiForSharePercentage(ProductColumnHeader.marketShareRevenue, productsPg);
    case ProductColumnHeader.retailerShareRevenue:
      return formatProductsKpiForSharePercentage(ProductColumnHeader.retailerShareRevenue, productsPg);
    case ProductColumnHeader.retailerMixVolume:
      return formatProductsKpiForSharePercentage(ProductColumnHeader.retailerMixVolume, productsPg);
    case ProductColumnHeader.retailerShareVolume:
      return formatProductsKpiForSharePercentage(ProductColumnHeader.retailerShareVolume, productsPg);
    case ProductColumnHeader.marketShareVolume:
      return formatProductsKpiForSharePercentage(ProductColumnHeader.marketShareVolume, productsPg);
    default:
      return '';
  }
};

const displayProductsChange = (headerKey: string, productsPg: any) => {
  switch (headerKey) {
    case ProductColumnHeader.retailerMixRevenue:
      return formatKpi(revenueShareType, productsPg[ProductColumnHeader.retailerMixRevenue]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    case ProductColumnHeader.retailerMixVolume:
      return formatKpi(revenueShareType, productsPg[ProductColumnHeader.retailerMixVolume]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    case ProductColumnHeader.marketShareRevenue:
      return formatKpi(revenueShareType, productsPg[ProductColumnHeader.marketShareRevenue]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    case ProductColumnHeader.marketShareVolume:
      return formatKpi(revenueShareType, productsPg[ProductColumnHeader.marketShareVolume]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    case ProductColumnHeader.retailerShareVolume:
      return formatKpi(revenueShareType, productsPg[ProductColumnHeader.retailerShareVolume]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    case ProductColumnHeader.retailerShareRevenue:
      return formatKpi(revenueShareType, productsPg[ProductColumnHeader.retailerShareRevenue]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    default:
      return '';
  }
};

const displayOppCell = (headerKey: string, oppContent: any) => {
  if (isOppShare(headerKey)) {
    return formatKpi(revenueShareType, oppContent[headerKey as OppColumnHeader]?.sharePercentage ?? 0);
  }
};

export const displayPrimaryCell = (
  headerKey: string,
  cellContent: any,
  currency: string,
  isForPAOrCL: boolean,
  selectedShareType?: ShareType,
) => {
  if (isVolumeColumn(headerKey)) {
    return formatKpi(volumeType, cellContent[headerKey as ProductGroupKpi]?.value ?? '');
  }

  if (isOppShare(headerKey)) {
    return displayOppCell(headerKey, cellContent) ?? '';
  } else if (isProductsShare(headerKey)) {
    return displayProductsCell(headerKey, cellContent) ?? '';
  }

  if (RevenueShareType.includes(headerKey)) {
    if (isRevenueShareMixColumns(headerKey)) {
      return formatKpiForSharePercentage(cellContent, isForPAOrCL, true, selectedShareType);
    }
    return formatKpiForSharePercentage(cellContent, isForPAOrCL, false, selectedShareType);
  }

  return formatKpi(revenueType, cellContent[headerKey as ProductGroupKpi]?.value ?? '', {
    currencySymbol: currency,
  });
};

export const formatKpiForActualGrowth = (
  headerKey: string,
  performanceAnalysisboardPg: any,
  currency: string,
): string => {
  if (RevenuePriceType.includes(headerKey)) {
    return formatKpi(revenueType, performanceAnalysisboardPg[headerKey as ProductGroupKpi]?.actual ?? '', {
      currencySymbol: currency,
      as: ValueType.ABSOLUTE_CHANGE,
    });
  }

  return formatKpi(volumeType, performanceAnalysisboardPg[headerKey as ProductGroupKpi]?.actual ?? '', {
    as: ValueType.ABSOLUTE_CHANGE,
  });
};

export const displayProductsSecondaryCell = (
  headerKey: string,
  value: any,
  selectedGrowthType: GrowthTypes,
  currency: string,
  displayOptions: DisplayOptions,
  isYtdMat: boolean = false,
) => {
  const overviewAndTrendsDisplayOptions = [DisplayOptions.overview, DisplayOptions.trends];
  if (overviewAndTrendsDisplayOptions.includes(displayOptions) || isYtdMat) {
    return undefined;
  }

  return displaySecondaryCell(headerKey, value, selectedGrowthType, currency, false);
};

export const displaySecondaryCell = (
  headerKey: string,
  performanceAnalysisboardPg: any,
  selectedGrowthType: GrowthTypes,
  currency: string,
  isForPA: boolean,
  selectedShareType?: ShareType,
) => {
  if (isOppShare(headerKey)) {
    return formatKpi(
      revenueShareType,
      performanceAnalysisboardPg[headerKey as OppColumnHeader]?.growthPercentage ?? 0,
      {
        as: ValueType.ABSOLUTE_CHANGE,
      },
    );
  }

  if (isKpiShare(headerKey) && !RevenueShareType.includes(headerKey)) {
    return displayProductsChange(headerKey, performanceAnalysisboardPg);
  }

  if (RevenueShareType.includes(headerKey)) {
    if (isRevenueShareMixColumns(headerKey)) {
      return formatKpiForAbsoluteGrowthPercentage(
        performanceAnalysisboardPg,
        selectedGrowthType,
        isForPA,
        true,
        selectedShareType,
      );
    }
    return formatKpiForAbsoluteGrowthPercentage(
      performanceAnalysisboardPg,
      selectedGrowthType,
      isForPA,
      false,
      selectedShareType,
    );
  }

  return selectedGrowthType === GrowthTypes.actual
    ? formatKpiForActualGrowth(headerKey, performanceAnalysisboardPg, currency)
    : formatKpi(revenueType, performanceAnalysisboardPg[headerKey as ProductGroupKpi]?.percentage ?? '', {
        as: ValueType.PERCENTAGE_CHANGE,
      });
};
const revenueKpiList: string[] = [
  ColumnHeaderForPA.yourRevenueMix,
  ColumnHeaderForPA.yourRevenueShare,
  ProductGroupKpiPercentage.YourRevenueShareMix,
];
const volumeKpiList = ['yourVolumeMix', 'yourVolumeShare', ProductGroupKpiPercentage.YourVolumeShareMix];
const formatKpiForAbsoluteGrowthPercentage = (
  performanceAnalysisboardPg: any,
  selectedGrowthType: GrowthTypes,
  isForPA: boolean,
  isRevenue: boolean,
  selectedShareType?: ShareType,
) => {
  let finalList: string[];

  if (isRevenue) {
    finalList = revenueKpiList;
  } else {
    finalList = volumeKpiList;
  }
  if (isForPA) {
    if (selectedShareType === ShareType.MyMix) {
      return formatKpi(revenueShareType, performanceAnalysisboardPg[finalList[0]]?.growthPercentage ?? 0, {
        as: ValueType.ABSOLUTE_CHANGE,
      });
    }
    return formatKpi(revenueShareType, performanceAnalysisboardPg[finalList[1]]?.growthPercentage ?? 0, {
      as: ValueType.ABSOLUTE_CHANGE,
    });
  }
  return formatKpi(revenueShareType, performanceAnalysisboardPg[finalList[2]]?.growthPercentage ?? 0, {
    as: ValueType.ABSOLUTE_CHANGE,
  });
};

const formatKpiForSharePercentage = (
  performanceAnalysisboardPg: any,
  isForPA: boolean,
  isRevenue: boolean,
  selectedShareType?: ShareType,
) => {
  let finalList: string[];

  if (isRevenue) {
    finalList = revenueKpiList;
  } else {
    finalList = volumeKpiList;
  }
  if (isForPA) {
    if (selectedShareType === ShareType.MyMix) {
      return formatKpi(revenueShareType, performanceAnalysisboardPg[finalList[0]]?.sharePercentage ?? 0);
    }
    return formatKpi(revenueShareType, performanceAnalysisboardPg[finalList[1]]?.sharePercentage ?? 0);
  }
  return formatKpi(revenueShareType, performanceAnalysisboardPg[finalList[2]]?.sharePercentage ?? 0);
};
